import React, { FunctionComponent, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, FormikProps, Field, FieldProps, Form, FormikHelpers } from 'formik';
import { Grid, Switch, Button } from '@inplayer-org/inplayer-ui';

// actions
import { loadJWPlayers, syncOvpVideos } from 'ducks/merchantDucks/items/actions';

// selectors
import { getJwPlayers } from 'ducks/merchantDucks/items/selectors';

// components
import Loader from 'components/Loader';
import Select from 'components/Fields/Select';
import StyledContainer from 'components/StyledContainer';

interface OwnProps {
  videosIds: Array<string>;
  syncAll: boolean;
  ovpName: string;
  accessControlType: number;
  closeModal: () => void;
  onBackButtonClick: () => void;
}

type Props = OwnProps;

interface FormValues {
  player_id: string;
  player_id_update_existing: boolean;
}

const { Container, Cell } = Grid;

const SyncJwPlayer: FunctionComponent<Props> = ({
  videosIds,
  syncAll,
  ovpName,
  accessControlType,
  closeModal,
  onBackButtonClick,
}) => {
  const dispatch = useDispatch();

  const { players, isFetching } = useSelector(getJwPlayers);

  const initialValues: FormValues = {
    player_id: '',
    player_id_update_existing: false,
  };

  useEffect(() => {
    if (!isFetching) {
      dispatch(loadJWPlayers());
    }
  }, []);

  const defaultOption = { displayName: 'Select video player', disabled: true };
  const jwVideos = players.map(({ key, name }: any) => ({
    value: key,
    displayName: name,
  }));

  const onFormSubmit = async (
    { player_id: playerId, player_id_update_existing: playerIdUpdateExisting }: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    await dispatch(
      syncOvpVideos({
        ovpName,
        videosIds,
        syncAll,
        isUpdate: false,
        accessControlType,
        playerId,
        playerIdUpdateExisting,
      })
    );
    setSubmitting(false);
    closeModal();
  };

  if (isFetching) {
    return <Loader />;
  }

  return (
    <Formik onSubmit={onFormSubmit} initialValues={initialValues}>
      {({ handleSubmit, values: { player_id }, isValid, isSubmitting }: FormikProps<FormValues>) => (
        <Form onSubmit={handleSubmit}>
          <StyledContainer margin="2rem" columns={1} rowGap="2rem">
            <Cell>
              <Container columns={1} rowGap="1rem">
                <Cell>
                  <Field
                    name="player_id"
                    label="Video Player"
                    value={player_id}
                    fullWidth
                    options={jwVideos}
                    defaultOption={defaultOption}
                    component={Select}
                    tag="dropdown_video_player"
                  />
                </Cell>
                <Cell>
                  <Field name="player_id_update_existing" fullWidth>
                    {({ field: { value, name }, form: { setFieldValue } }: FieldProps) => (
                      <Switch
                        id="player_id_update_existing"
                        label="Some of your selected videos may already exist. Do you want to apply the selected Player ID to them too?"
                        checked={value}
                        onChange={(checked: boolean) => setFieldValue(name, checked)}
                      />
                    )}
                  </Field>
                </Cell>
              </Container>
            </Cell>
          </StyledContainer>
          <StyledContainer columns={3} margin="3rem 2rem 0 2rem">
            <Cell>
              <Button
                tag="button_back"
                fullWidth
                buttonModifiers={['buttonLink']}
                type="button"
                onClick={onBackButtonClick}
              >
                Back
              </Button>
            </Cell>
            <Cell left={3}>
              <Button
                tag="button_sync_videos"
                fullWidth
                buttonModifiers={['buttonPrimary']}
                disabled={!isValid || isSubmitting || player_id === ''}
                type="submit"
              >
                Sync Videos
              </Button>
            </Cell>
          </StyledContainer>
        </Form>
      )}
    </Formik>
  );
};

export default SyncJwPlayer;
